import React, { useEffect, useState, useContext } from "react";
import { Row, Col, Form, Button, Badge } from "react-bootstrap";
import { MainContext } from "../../App";
// Components
import { goTo } from "../../components/global/GlobalTools";
import { useTranslation } from "react-i18next";
// Modals
import ModalSaveFeatureFlag from "./modals/ModalSaveFeatureFlag";
// API
import { GetFeatureFlags, UpdateFeatureFlag } from "../../api/SuperAdmins";

/**
 * Description: Component for displaying a lazy loading view
 * @return {JSX.Element}
 */
const LazyLoadingView = () => {
  return (
    <Row>
      <Col lg={{ span: 10, offset: 1 }} xxl={{ span: 8, offset: 2 }}>
        <Row className="table-lazyLoading">
          <Col md={{ span: 3, offset: 9 }} className="text-center mt-2 mb-1">
            <div style={customStyles.lazyButton} />
          </Col>
          {Array.from(Array(4).keys()).map((i) => (
            <Col md={12} key={i} className="text-center mt-3">
              <div style={customStyles.lazy} />
            </Col>
          ))}
        </Row>
      </Col>
    </Row>
  );
};

/**
 * Description: Custom styles for the feature flags view
 */
const customStyles = {
  flagsContainer: {
    maxHeight: "65vh",
    overflowY: "auto",
  },
  rowContainer: {
    background: "#ffffff",
    border: "1px solid #ececee",
    borderRadius: "8px",
    height: "60px",
    maxHeight: "120px",
    marginBlock: "10px",
  },
  lazy: {
    height: "60px",
  },
  lazyButton: {
    height: "28px",
  },
  badgeRelease: {
    width: "50%",
    maxWidth: "60%",
  },
  textFeatureFlag: {
    lineBreak: "anywhere",
  },
};

export default function FeatureFlagsView() {
  const [t] = useTranslation(["superAdmin", "global"]);
  const { permissions } = useContext(MainContext);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [featureFlags, setFeatureFlags] = useState([]);
  const [showFeatureFlagModal, setShowFeatureFlagModal] = useState(false);
  const [featureFlagToEdit, setFeatureFlagToEdit] = useState(null);

  /**
   * Initial loading
   */
  useEffect(() => {
    getFeatureFlags();
  }, []);

  /**
   * Description: Format the feature flag object for display
   * @param {object} [item={}]
   * @return {object}
   */
  const formatFeatureFlag = (item = {}) => {
    return {
      id: item.id,
      name: item.name,
      description: item.description || "",
      isAvailable: item.is_active == "1",
      isRelesed: item.is_active_globally == "1",
      organizations: item.organizations?.map((org) => ({
        label: org.name,
        value: org.id,
      })),
      roles: item.groups?.map((role) => ({
        label: role.name,
        value: role.id,
      })),
      percentage: item.percentage || "",
    };
  };

  /**
   * Description: Get all feature flags
   */
  const getFeatureFlags = () => {
    GetFeatureFlags()
      .then((response) => {
        if (response?.status) {
          let featureFlags = [];
          const data = response.data || [];
          featureFlags = data.map((item) => {
            return formatFeatureFlag(item);
          });
          setFeatureFlags(featureFlags);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  /**
   * Description: Function to update the feature flag status
   * @param {object} feature
   */
  const activateFeature = (feature) => {
    const payload = {
      id: feature.id,
      description: feature.description || "",
      organization_ids: feature.organizations?.map((org) => org.value),
      group_ids: feature.roles?.map((role) => role.value),
      is_active: feature.is_active,
    };
    setIsSaving(true);
    UpdateFeatureFlag(payload)
      .then((response) => {
        if (response?.status) {
          const data = response.data || {};
          // Update the feature flag in the list
          setFeatureFlags((prevFlags) => {
            const index = prevFlags.findIndex((flag) => flag.id == data.id);
            if (index === -1) return prevFlags;
            const updatedFlag = {
              ...prevFlags[index],
              isAvailable: data.is_active == "1",
              isRelesed: data.is_active_globally == "1",
            };
            const newFlags = [...prevFlags];
            newFlags[index] = updatedFlag;
            return newFlags;
          });
        }
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return permissions.admin_feature_flags?.access ? (
    isLoading ? (
      <LazyLoadingView />
    ) : (
      <>
        <Row>
          {permissions.admin_feature_flags?.create && (
            <Col
              className="mt-2 p-0"
              lg={{ span: 10, offset: 1 }}
              xxl={{ span: 8, offset: 2 }}
            >
              <Button
                variant="primary"
                className="float-end"
                onClick={() => setShowFeatureFlagModal(true)}
              >
                {t("featureFlagsView.createFeature")}
              </Button>
            </Col>
          )}
          <Col
            className="mt-3"
            lg={{ span: 10, offset: 1 }}
            xxl={{ span: 8, offset: 2 }}
            style={customStyles.flagsContainer}
          >
            {featureFlags.map((feature) => {
              return (
                <Row
                  key={feature.id}
                  className="mb-3"
                  style={customStyles.rowContainer}
                >
                  <Col xs={8} className="mt-3 mb-3">
                    <span
                      className="fw-bold"
                      style={customStyles.textFeatureFlag}
                    >
                      {feature.name}
                    </span>
                    {permissions.admin_feature_flags?.edit && (
                      <span
                        className="ms-3 bi bi-pencil-fill c-pointer"
                        onClick={() => {
                          setFeatureFlagToEdit(feature);
                          setShowFeatureFlagModal(true);
                        }}
                      />
                    )}
                  </Col>
                  <Col xs={4} className="mt-3">
                    <Badge
                      bg={feature.isRelesed ? "success" : "secondary"}
                      style={customStyles.badgeRelease}
                    >
                      {t(
                        feature.isRelesed
                          ? "superAdmin:featureFlagsView.released"
                          : "superAdmin:featureFlagsView.notReleased"
                      )}
                    </Badge>
                    <Form className="float-end">
                      <Form.Check
                        className=""
                        type="switch"
                        id="custom-switch"
                        checked={feature.isAvailable}
                        disabled={isSaving}
                        onChange={(e) =>
                          activateFeature({
                            ...feature,
                            is_active: e.target.checked ? 1 : 0,
                          })
                        }
                      />
                    </Form>
                  </Col>
                </Row>
              );
            })}
          </Col>
        </Row>
        {showFeatureFlagModal && (
          <ModalSaveFeatureFlag
            show={showFeatureFlagModal}
            onHide={() => {
              setShowFeatureFlagModal(false);
              setFeatureFlagToEdit(null);
            }}
            feature={featureFlagToEdit}
            setFeatureFlags={setFeatureFlags}
            formatFeatureFlag={formatFeatureFlag}
          />
        )}
      </>
    )
  ) : (
    goTo("/dashboard")
  );
}
